import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { lazyInject } from '@app/containers/base/decorators';
import {
  GRID_LIST_VIEW_TYPE,
  IBaseEntityResponse,
  IBaseEntityType,
  IDataOptions,
  IEvent,
  IGridFlatInventoryItemVenue,
  IGridFlatInventoryItemVenueTotal,
  IQohInventoryItemVenue,
  MergeRowsConfig,
  TOTALS_CATEGORY,
  TRANSFER_PDF_HEADER_VALUES,
  TRANSFER_PDF_HEADERS,
  TRANSFER_PDF_HEADERS_FINAL,
  TRANSFER_TYPE,
} from '@app/shared/types';
import { flatTreeDataByHasChildren } from '@app/shared/utils';
import { ENTITY_ID_PATH, ITEM_ENTITY_ID_PATH } from '@config/global-constants';
import {
  concat,
  filter,
  find,
  forEach,
  get,
  has,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  map,
  merge,
  reduce,
  set,
  sortBy,
} from 'lodash';
import { CompanyUtilService } from '../company';
import { NumberFormatService } from '../number-format';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import {
  calculatePendingQuantity,
  calculateUnitCost,
  filterQohFromColumn,
  getCurrentItemQoh,
  getIsShowReceivedColStatus,
  addParQtyColumn,
  getParQty,
  addQtyPulledColumn,
} from './transfer.util';
import { DecimalNumber } from '@app/shared/common';
import { PdfBaseService } from '@app/shared/common/make-pdf/pdf.base.service';
import { TRANSFER_FORM_FIELD_PATHS } from './transfer-form-field-paths';
import { TransferTotalFormService } from './transfer-totals-form.service';
import {
  IPdfDefinitions,
  IPdfOptions,
  PdfGenerateServiceV2,
  ViewSupportType,
} from '@app/shared/common/make-pdf/v2';
import { sortAndFilterData } from '@app/shared/common/make-pdf/v2/utils';
import { TransferEditContainerComponent } from '@app/containers/transfer/containers';
import { getTransferPdfConfigBuilder } from './transfer-pdf-config';
import { XprtDataService } from '@shared/services/xprt-data/xprt-data.service';
import { StateService } from '../state-service';
import { TransferGroupChildEditContainerComponent } from '@app/containers/transfer-group/containers';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferPdfService {
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(PdfBaseService) public pdfBaseService: PdfBaseService;
  @lazyInject(TransferTotalFormService)
  public totalFromService: TransferTotalFormService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;
  public pdfEntityString: string = 'Transfer Summary';
  private pendingQtyMap: IBaseEntityType;
  private qohData: IQohInventoryItemVenue[];
  private events: IEvent[];

  public constructor(public injector: Injector) {}

  public setPendingQty(pendingQtyMap: IBaseEntityType): void {
    this.pendingQtyMap = pendingQtyMap;
  }

  public setQOHData(qohData: IQohInventoryItemVenue[]): void {
    this.qohData = qohData;
  }

  public getQOHData(): IQohInventoryItemVenue[] {
    return this.qohData;
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async handlePdfClick(
    editor:
      | TransferEditContainerComponent
      | TransferGroupChildEditContainerComponent,
    action: 'print' | 'export',
    useDocument: boolean = false,
    options?: {
      isTransferGroup?: boolean;
    },
  ): Promise<void> {
    const { isTransferGroup = false } = options || {};
    const currentStatus: TRANSFER_STATUS_VALUE = editor.getFormFieldValue(
      editor.formFieldPath.status,
    );

    this.events = await this.getEvents();
    const isNotFinalTransfer = this.isNotFinalTransfer(
      currentStatus,
      editor.form.value.transfer_type,
    );
    let modalColumns = isNotFinalTransfer
      ? [...TRANSFER_PDF_HEADERS]
      : [...TRANSFER_PDF_HEADERS_FINAL];
    if (!editor.showQohFrom) {
      modalColumns = filterQohFromColumn(modalColumns);
    }

    modalColumns = addParQtyColumn(modalColumns, currentStatus);
    modalColumns = addQtyPulledColumn(modalColumns, {
      transferStatus: currentStatus,
      preColumnPath: isNotFinalTransfer
        ? TRANSFER_PDF_HEADER_VALUES.Quantity
        : TRANSFER_PDF_HEADER_VALUES.InitialQuantity,
    });

    const totalFieldValue = !includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      currentStatus,
    );

    const pdfConfig = getTransferPdfConfigBuilder(TRANSFER_FORM_FIELD_PATHS, {
      isFromDetail: true,
      signatureConfiguration: {
        title: {
          text: I18n.Common.Notes,
        },
      },
      totalsConfiguration: {
        enabled: true,
        getDefaultValue: () => totalFieldValue,
      },
    })(
      async (definitions: IPdfDefinitions) => {
        const isFromDetail = true;
        let doc = merge(
          { internal_number: editor.document?.internal_number },
          editor.form.value,
        );
        if (useDocument) {
          doc = merge({}, editor.document, {
            [TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues]: get(
              editor.form.value,
              TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
            ),
            [TRANSFER_FORM_FIELD_PATHS.transferGroupName]: get(
              editor.form.value,
              TRANSFER_FORM_FIELD_PATHS.transferGroupName,
            ),
          });
        }
        const docEvent = find(
          this.events,
          (event: IEvent) => event.id === doc.event_id,
        );
        set(doc, 'event_id', docEvent?.name);
        set(doc, 'displayQoh', editor.showQohFrom);
        this.setPendingQty(editor.entityFormService.pendingQuantityData);
        this.setQOHData(editor.entityFormService.qohData);

        return this.getPdfData(
          doc,
          {
            isFromDetail,
            docEvent,
            xuxGrid: editor.entityFormService.xuxGrid,
          },
          definitions?.options,
        );
      },
      modalColumns,
      null,
      {
        isTransferGroup,
        i18nService: this.i18nService,
      },
    );
    const actionMapping = {
      print: this.pdfGenerateServiceV2.printPdfAsync.bind(
        this.pdfGenerateServiceV2,
      ),
      export: this.pdfGenerateServiceV2.downloadPdfAsync.bind(
        this.pdfGenerateServiceV2,
      ),
    };
    await actionMapping[action](pdfConfig);
  }

  public async getPdfData(
    doc: IBaseEntityType,
    dataOptions: IDataOptions = {},
    options: IPdfOptions = {},
  ): Promise<IBaseEntityType> {
    const isOpenOrPendingTransfer = includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      doc[TRANSFER_FORM_FIELD_PATHS.status],
    );
    const { xuxGrid, isFromDetail = true, docEvent } = dataOptions;
    const gridFilterString = get(
      xuxGrid,
      'xuxGridComponent.actionPanel.quickFilterControl.value',
    );
    const docTransferType = doc[TRANSFER_FORM_FIELD_PATHS.transferType];
    const displayQoh = doc['displayQoh'];
    const docStatus = doc[TRANSFER_FORM_FIELD_PATHS.status];
    const isTreeView = isEqual(
      this.stateService.getTransferItemListViewSetting(),
      GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
    );
    const isOutsideOfEventTransfer = isEqual(
      TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
      docTransferType,
    );

    const flatInventoryItems: IGridFlatInventoryItemVenue[] =
      flatTreeDataByHasChildren(get(doc, 'inventory_items'));
    let valuePath = ENTITY_ID_PATH;
    flatInventoryItems.forEach((item: IGridFlatInventoryItemVenue) => {
      if (isNil(item.entity_id)) {
        item.entity_id = item.item_entity_id;
        valuePath = ITEM_ENTITY_ID_PATH;
      }
    });
    const inventoryItemVenuesData: IQohInventoryItemVenue[] = isFromDetail
      ? sortAndFilterData(
          flatInventoryItems,
          {
            baseOnGrid: xuxGrid,
            viewSupport: ViewSupportType.sortAndFilter,
          },
          ENTITY_ID_PATH,
          valuePath,
        )
      : get(doc, TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues);
    const filteredData: IQohInventoryItemVenue[] = filter(
      inventoryItemVenuesData,
      (item) => {
        const initialCount =
          item[TRANSFER_FORM_FIELD_PATHS.initialCount] || '0';
        const count = item[TRANSFER_FORM_FIELD_PATHS.count] || '0';

        return (
          (!isNil(initialCount) && !isEqual(initialCount, '0')) ||
          (getIsShowReceivedColStatus(docTransferType, docStatus) &&
            !isNil(count) &&
            !isEqual(count, '0'))
        );
      },
    );

    // Add/Update some fields
    if (isFromDetail) {
      const siteId: string = get(doc, 'site_id');
      const siteIdTo: string = get(doc, 'site_id_to');
      if (
        includes(
          [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
          docStatus,
        )
      ) {
        this.setQOHToDocument(filteredData, this.qohData, siteId, siteIdTo);
        this.setPendingQtyToDocument(filteredData, this.pendingQtyMap);
      }
      this.appendExtraInfo(filteredData, docEvent, isOutsideOfEventTransfer);
    }

    const document: IBaseEntityType = {
      displayQoh,
      isTreeView, // only for csv
      gridFilterString,
      internal_number: doc.internal_number,
      isNotFinalTransfer: this.isNotFinalTransfer(docStatus, docTransferType),
      isVisibilityRequestCost: getIsShowReceivedColStatus(
        docTransferType,
        docStatus,
      ),
      [TRANSFER_FORM_FIELD_PATHS.status]: docStatus,
      [TRANSFER_FORM_FIELD_PATHS.transferType]: docTransferType,
      [TRANSFER_FORM_FIELD_PATHS.transactionDate]:
        doc[TRANSFER_FORM_FIELD_PATHS.transactionDate],
      [TRANSFER_FORM_FIELD_PATHS.siteId]: doc[TRANSFER_FORM_FIELD_PATHS.siteId],
      [TRANSFER_FORM_FIELD_PATHS.siteName]:
        doc[TRANSFER_FORM_FIELD_PATHS.siteName],
      [TRANSFER_FORM_FIELD_PATHS.siteIdTo]:
        doc[TRANSFER_FORM_FIELD_PATHS.siteIdTo],
      [TRANSFER_FORM_FIELD_PATHS.siteNameTo]:
        doc[TRANSFER_FORM_FIELD_PATHS.siteNameTo],
      [TRANSFER_FORM_FIELD_PATHS.storeNumber]:
        doc[TRANSFER_FORM_FIELD_PATHS.storeNumber],
      [TRANSFER_FORM_FIELD_PATHS.storeNumberTo]:
        doc[TRANSFER_FORM_FIELD_PATHS.storeNumberTo],
      [TRANSFER_FORM_FIELD_PATHS.eventId]:
        doc[TRANSFER_FORM_FIELD_PATHS.eventId],
      [TRANSFER_FORM_FIELD_PATHS.source]: doc[TRANSFER_FORM_FIELD_PATHS.source],
      [TRANSFER_FORM_FIELD_PATHS.is_send]:
        doc[TRANSFER_FORM_FIELD_PATHS.is_send],
      [TRANSFER_FORM_FIELD_PATHS.transferGroupName]: get(
        doc,
        TRANSFER_FORM_FIELD_PATHS.transferGroupName,
        '',
      ),
      virtual: {},
    };

    const formValue = {
      status: docStatus,
      transfer_type: doc[TRANSFER_FORM_FIELD_PATHS.transferType],
      site_department_code_to: doc[TRANSFER_FORM_FIELD_PATHS.departmentCodeTo],
    };
    const includeDepartmentCodeRow = !!find(options.selectedColumns, (column) =>
      isEqual(column.title, I18n.EntityNames.DepartmentCode),
    );
    const noSkipEmptyValue = true;
    const totals = this.totalFromService.mapItemDataToTotalsData(
      flatInventoryItems,
      TRANSFER_FORM_FIELD_PATHS,
      formValue,
      noSkipEmptyValue,
      null,
      includeDepartmentCodeRow,
    );

    const pinnedBottomData = map(totals.pinnedBottomData, (bottomData) => ({
      ...bottomData,
      isPinnedBottom: true,
    }));

    // Update unit cost when transfer unit changed
    if (
      includes(
        [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
        docStatus,
      )
    ) {
      forEach(filteredData, (item: IQohInventoryItemVenue) => {
        const params = { data: item };
        item.item_cost = calculateUnitCost(params);
      });
    }
    const flatRows = this.flatTotalRows(totals.normalRows, {
      isOpenOrPendingTransfer,
    });
    set(
      document,
      TRANSFER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
      filteredData,
    );
    set(
      document,
      TRANSFER_FORM_FIELD_PATHS.virtualTotals,
      concat(flatRows, pinnedBottomData),
    );

    return document;
  }

  public isNotFinalTransfer(status: string, transferType: string): boolean {
    const isOpenOrCanceled = includes(
      [TRANSFER_STATUS_VALUE.Canceled, TRANSFER_STATUS_VALUE.Open],
      status,
    );
    const isPreEventTransfer = isEqual(
      transferType,
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
    );

    return isOpenOrCanceled || isPreEventTransfer;
  }

  public setEvents(events: IEvent[]): void {
    this.events = events;
  }

  public async getEvents(): Promise<IEvent[]> {
    if (!this.events) {
      this.events = await this.xprtDataService
        .getEventsByFilter({
          select: ['name', 'id', 'event_type_entity_id'],
          orderby: 'name asc',
        })
        .toPromise();
    }

    return this.events;
  }

  public appendExtraInfo(
    items: IQohInventoryItemVenue[],
    selectedEvent: IEvent,
    isOutsideOfEventTransfer: boolean,
  ): void {
    forEach(items, (item) => {
      item.gl_account_name = get(item, 'gl_account.name');
      item.major_category_name = get(item, 'reporting_category_major.name');
      item.minor_category_name = get(item, 'reporting_category_minor.name');
      forEach(item.measures, (measure) => {
        if (measure.entity_id === item.measure_entity_id) {
          item.measure_description = measure.description;
          item.measure_factor_value = measure.factor_value;
        }
      });
      const parQty = getParQty(item, selectedEvent, isOutsideOfEventTransfer);
      set(item, 'par_qty', parQty);
    });
  }

  public setPendingQtyToDocument(
    items: IQohInventoryItemVenue[],
    pendingQtyMap: IBaseEntityType,
  ): void {
    if (!pendingQtyMap) {
      return;
    }
    forEach(items, (item) => {
      if (pendingQtyMap[item.entity_id]) {
        item.pending_qty_from = pendingQtyMap[item.entity_id].site_id;
        item.pending_qty_to = pendingQtyMap[item.entity_id].site_id_to;
      }
    });
  }

  public setQOHToDocument(
    items: IQohInventoryItemVenue[],
    qohData: IQohInventoryItemVenue[],
    siteId,
    siteIdTo,
  ): IQohInventoryItemVenue[] {
    forEach(items, (item) => {
      item.qoh_from = get(getCurrentItemQoh(qohData, siteId, item), 'count', 0);
      item.qoh_to = get(getCurrentItemQoh(qohData, siteIdTo, item), 'count', 0);
    });

    return items;
  }

  /**
   * @deprecated should use gtePendingQuantityMap in replenish utils instead
   */
  public createPendingQuantityMap(
    pendingQtyData: IBaseEntityResponse,
    siteId: string,
    siteIdTo: string,
  ): IBaseEntityType {
    const pendingQtyMap = {};
    forEach(pendingQtyData.items, (transfer) => {
      forEach(transfer.inventory_items, (inventoryItem) => {
        if (!has(pendingQtyMap, inventoryItem['item_entity_id'])) {
          pendingQtyMap[inventoryItem['item_entity_id']] = {
            site_id: new DecimalNumber(0),
            site_id_to: new DecimalNumber(0),
          };
        }
        const pendingQuantity = calculatePendingQuantity(
          inventoryItem['initial_count'],
          inventoryItem['measure_factor_value'],
        );
        // Calculate Pending Quantities From
        // If the site_id of the transfer is equal to the siteId, is mean this item transfer to other site.
        if (isEqual(siteId, transfer['site_id'])) {
          pendingQtyMap[inventoryItem['item_entity_id']]['site_id'] =
            pendingQtyMap[inventoryItem['item_entity_id']]['site_id'].sub(
              pendingQuantity,
            );
        }
        // If the site_id_to of the transfer is equal to the site_id, is mean quantity is transfer in from other site.
        if (isEqual(siteId, transfer['site_id_to'])) {
          pendingQtyMap[inventoryItem['item_entity_id']]['site_id'] =
            pendingQtyMap[inventoryItem['item_entity_id']]['site_id'].add(
              pendingQuantity,
            );
        }
        // Calculate Pending Quantities To
        // If the site_id_to of the transfer is equal to the site_id, is mean this item transfer to other site.
        if (isEqual(siteIdTo, transfer['site_id'])) {
          pendingQtyMap[inventoryItem['item_entity_id']]['site_id_to'] =
            pendingQtyMap[inventoryItem['item_entity_id']]['site_id_to'].sub(
              pendingQuantity,
            );
        }
        // If the site_id_to of the transfer is equal to the site_id_to,
        // is mean quantity is transfer in from other site.
        if (isEqual(siteIdTo, transfer['site_id_to'])) {
          pendingQtyMap[inventoryItem['item_entity_id']]['site_id_to'] =
            pendingQtyMap[inventoryItem['item_entity_id']]['site_id_to'].add(
              pendingQuantity,
            );
        }
      });
    });

    return pendingQtyMap;
  }

  public flatTotalRows(
    rows: IGridFlatInventoryItemVenueTotal[],
    flattenOptions?: {
      isOpenOrPendingTransfer?: boolean;
    },
  ): IGridFlatInventoryItemVenueTotal[] {
    const { isOpenOrPendingTransfer = false } = flattenOptions || {};
    const flatRows: IGridFlatInventoryItemVenueTotal[] = [];

    forEach(rows, (row: IGridFlatInventoryItemVenueTotal) => {
      flatRows.push(row);

      if (row.children) {
        row.category = row.site_department_code_to;
        row.site_department_code_to = '';

        const rowsToPush = isOpenOrPendingTransfer
          ? [this.getTransferSumTotalRowByCategory(row)]
          : sortBy(row.children, 'name');
        flatRows.push(...rowsToPush.filter(Boolean));
      }
    });

    return flatRows;
  }

  private sumNormalChildrenRows(
    row: IGridFlatInventoryItemVenueTotal,
  ): IGridFlatInventoryItemVenueTotal {
    return this.mergeChildrenRows(row.children, {
      fieldsToSum: [
        'initial_count',
        'count',
        'total_cost',
        'received_total_cost',
      ],
      defaultValues: {
        name: '',
        count: new DecimalNumber(0),
        initial_count: new DecimalNumber(0),
        total_cost: new DecimalNumber(0),
        received_total_cost: new DecimalNumber(0),
      },
    });
  }

  private sumReversedChildrenRows(
    row: IGridFlatInventoryItemVenueTotal,
  ): IGridFlatInventoryItemVenueTotal {
    return this.mergeChildrenRows(row.children, {
      fieldsToSum: [
        'quantity_reversed',
        'reversed_cost',
        'count',
        'received_total_cost',
      ],
      defaultValues: {
        name: '',
        count: new DecimalNumber(0),
        quantity_reversed: new DecimalNumber(0),
        reversed_cost: new DecimalNumber(0),
        received_total_cost: new DecimalNumber(0),
      },
    });
  }

  private mergeChildrenRows(
    children: IGridFlatInventoryItemVenueTotal[],
    config: MergeRowsConfig,
  ): IGridFlatInventoryItemVenueTotal {
    const { fieldsToSum, defaultValues } = config;

    return reduce(
      children,
      (acc, child) => {
        forEach(fieldsToSum, (field) => {
          if (!isEmpty(child[field])) {
            acc[field] = acc[field].add(child[field]);
          }
        });

        return acc;
      },
      {
        name: '',
        site_department_code_to: '',
        ...defaultValues,
      },
    );
  }

  /**
   * @description Get transfer sum total row for each category.
   */
  private getTransferSumTotalRowByCategory(
    row: IGridFlatInventoryItemVenueTotal,
  ): IGridFlatInventoryItemVenueTotal {
    if (isEmpty(row.children)) {
      return null;
    }

    switch (row.categoryType) {
      case TOTALS_CATEGORY.normal:
        return this.sumNormalChildrenRows(row);
      case TOTALS_CATEGORY.reversals:
        return this.sumReversedChildrenRows(row);
      default:
        return null;
    }
  }
}
